import { RouteLocationNormalizedLoaded, RouteRecordRaw } from 'vue-router';
import { useBrandComponent } from '@/composables/useBrandComponent';
import { tutorialsRoutes } from '@/modules/tutorials/router/tutorials';
import { miTableroRoutes } from '@/modules/mi-tablero/router/miTablero';
import { policiesRoutes } from '@/modules/policies-v2/router/policies';
import { clientsRoutes } from '@/modules/clients-v2/router/clients';
import { activityLogsRoutes } from '@/modules/activity-logs-v2/router/activityLogs';
import { profileRoutes } from '@/modules/profile-v2/router/profile';
import { supportV2Routes } from '@/modules/support-v2/router/support';
import {
    redirectToTablero,
    redirectToFoliosList,
    redirectActivityDetailById,
    redirectActivityDetailByHash,
    redirectToPoliciesList,
    redirectPolicyEditByHash,
    redirectToClientsList,
    redirectClientEditByHash,
    redirectToProfile,
    redirectToProfileClaves,
    redirectToSupportList
} from './v1ToV2';

const MainRoutes: RouteRecordRaw = {
    path: '/private',
    meta: {
        requiresAuth: true
    },
    redirect: { name: 'Mi Tablero v2' },
    component: () => import('@/layouts/full/FullLayout.vue'),
    children: [
        {
            path: 'user',
            name: 'Usuario',
            redirect: { name: 'Profile' },
            children: [
                {
                    path: 'profile',
                    name: 'Profile',
                    // v1 cerrada (cierre de rediseño): Mi Perfil v2 es el destino único.
                    // El componente v1 (`Profile.vue`) NO se borra, solo deja de montarse
                    // desde esta ruta.
                    beforeEnter: redirectToProfile,
                    component: useBrandComponent('views/private/user/profile/Profile.vue'),
                    meta: { requiresPermission: 'brokerin', minLevel: 20, requiresSystemPermission: 'profile' }
                }
            ]
        },
        // {
        //     path: 'dashboard',
        //     name: 'Mi Tablero',
        //     redirect: { name: 'dashboard-main' },
        //     children: [
        //         {
        //             name: 'dashboard-main',
        //             path: 'main',
        //             component: () => import('@/views/private/dashboard/dashboard2.vue'),
        //             meta: { requiresPermission: 'brokerin', minLevel: 20 }
        //         }
        //     ]
        // },
        {
            path: 'dashboard',
            name: 'Mi Tablero',
            redirect: { name: 'dashboard-main-2' },
            children: [
                {
                    name: 'dashboard-main-2',
                    path: 'main',
                    // v1 cerrada (cierre de rediseño): Mi Tablero v2 es el destino único.
                    // `dashboard1.vue` NO se borra, solo deja de montarse desde esta ruta.
                    beforeEnter: redirectToTablero,
                    component: useBrandComponent('views/private/dashboard/dashboard1.vue'),
                    meta: { requiresPermission: 'brokerin', minLevel: 20, requiresSystemPermission: 'my_board' }
                }
            ]
        },
        {
            path: 'activity',
            name: 'Actividades',
            redirect: { name: 'Mis Folios' },
            children: [
                {
                    path: 'list',
                    name: 'Mis Folios',
                    // v1 cerrada: Mis Folios v2 es el destino único.
                    beforeEnter: redirectToFoliosList,
                    component: () => import('@/views/private/activity/List.vue'),
                    props: (route: RouteLocationNormalizedLoaded) => ({
                        searchInput: route.query.searchInput || '',
                        status: route.query.status || null,
                        sub_status: route.query.sub_status || null,
                        movementType: route.query.movementType || null,
                        movementCategory: route.query.movementCategory || null,
                        insurance_id: route.query.insurance_id || null,
                        subcategoryId: route.query.subcategoryId || null,
                        filterRageDates: route.query.filterRageDates || null,
                        alert: route.query.alert || null,
                        rag: route.query.rag || null,
                        model: route.query.model || null,
                        pending_status: route.query.pending_status || null,
                        end_date: route.query.end_date || null
                    }),
                    meta: { requiresPermission: 'brokerin', minLevel: 20, requiresSystemPermission: 'my_folios' }
                },
                {
                    path: 'detail/:activity_id',
                    name: 'Detalle de Actividad',
                    // v1 cerrada: solo trae un id numérico, sin hash traducible en el
                    // guard → cae a la LISTA de Mis Folios v2 (mapa aprobado).
                    beforeEnter: redirectActivityDetailById,
                    component: () => import('@/views/private/activity/DetailV2.vue'),
                    meta: { requiresPermission: 'brokerin', minLevel: 20, requiresSystemPermission: 'my_folios' }
                },
                {
                    path: 'detail/v2/:hash_folio',
                    name: 'Detalle de la Actividad',
                    // v1 cerrada: sí trae hash → va directo al detalle v2 con ese hash.
                    beforeEnter: redirectActivityDetailByHash,
                    component: () => import('@/views/private/activity/DetailV2.vue'),
                    meta: { requiresPermission: 'brokerin', minLevel: 20, requiresSystemPermission: 'my_folios' }
                }
            ]
        },
        {
            path: 'quote',
            name: 'Cotizaciones',
            redirect: { name: 'Mis Cotizaciones' },
            children: [
                {
                    name: 'Mis Cotizaciones',
                    path: 'list',
                    // v1 cerrada: no tiene equivalente propio en v2 → Mis Folios v2 (mapa aprobado).
                    beforeEnter: redirectToFoliosList,
                    component: () => import('@/views/private/activity/ListOt.vue'),
                    props: (route: RouteLocationNormalizedLoaded) => ({
                        searchInput: route.query.searchInput || '',
                        status: route.query.status || null,
                        movementType: '10',
                        insurance_id: route.query.insurance_id || null,
                        subcategoryId: route.query.sub_category_id || null,
                        filterRageDates: route.query.filterRageDates || null,
                        alert: route.query.alert || null,
                        model: ''
                    }),
                    meta: { requiresPermission: 'brokerin', minLevel: 20, requiresSystemPermission: 'quote_by_work_order' }
                }
            ]
        },
        {
            path: 'policy',
            name: 'Polizas',
            redirect: { name: 'Mis Pólizas' },
            children: [
                {
                    name: 'Mis Pólizas',
                    path: 'list',
                    // v1 cerrada: Mis Pólizas v2 es el destino único.
                    beforeEnter: redirectToPoliciesList,
                    component: () => import('@/views/private/policy/List.vue'),
                    props: (route: RouteLocationNormalizedLoaded) => ({
                        searchInput: route.query.searchInput || '',
                        client_hash: route.query.client_hash || '',
                        insurance_id: route.query.insurance_id || '',
                        filterRageDates: route.query.filterRageDates || null,
                        date_field: route.query.date_field || '',
                        payment_status: route.query.payment_status || null,
                        renovation_status: route.query.renovation_status || null,
                        policy_status: route.query.policy_status || null,
                        kind: route.query.kind || null,
                        sub_category_id: route.query.sub_category_id || null,
                        attachment: route.query.attachment || null,
                        category_type: route.query.category_type || ''
                    }),
                    meta: { requiresPermission: 'brokerin', minLevel: 20, requiresSystemPermission: 'my_policies' }
                },
                {
                    name: 'Editar Póliza',
                    path: 'edit/:hash/:isPylc(0|1)?/:isRenovation(0|1)?',
                    // v1 cerrada: trae hash → detalle de Mis Pólizas v2 vía query.hash
                    // (mismo mecanismo que usa `useActivityLogs.ts` para navegar ahí).
                    beforeEnter: redirectPolicyEditByHash,
                    component: () => import('@/views/private/policy/Edit.vue'),
                    meta: { requiresPermission: 'brokerin', minLevel: 20, requiresSystemPermission: 'my_policies' }
                },
                {
                    name: 'Editar Póliza Renovación',
                    path: 'edit-renovation/:hash/:isPylc(0|1)?',
                    // v1 cerrada: mismo destino que "Editar Póliza" (detalle de póliza v2).
                    beforeEnter: redirectPolicyEditByHash,
                    component: () => import('@/views/private/policy/EditRenovation.vue'),
                    meta: { requiresPermission: 'brokerin', minLevel: 20, requiresSystemPermission: 'my_policies' }
                }
            ]
        },
        {
            path: 'client',
            name: 'Clientes',
            redirect: { name: 'Mis Clientes' },
            children: [
                {
                    name: 'Mis Clientes',
                    path: 'list',
                    // v1 cerrada: Mis Clientes v2 es el destino único.
                    beforeEnter: redirectToClientsList,
                    component: () => import('@/views/private/client/List.vue'),
                    props: (route: RouteLocationNormalizedLoaded) => ({
                        searchInput: route.query.searchInput || ''
                    }),
                    meta: { requiresPermission: 'brokerin', minLevel: 20, requiresSystemPermission: 'my_clients' }
                },
                {
                    name: 'Editar Cliente',
                    path: 'edit/:client_hash',
                    // v1 cerrada: trae hash → detalle de Mis Clientes v2.
                    beforeEnter: redirectClientEditByHash,
                    component: () => import('@/views/private/client/Edit.vue'),
                    meta: { requiresPermission: 'brokerin', minLevel: 20, requiresSystemPermission: 'my_clients' }
                }
            ]
        },
        {
            path: 'emission',
            name: 'Emisiones',
            redirect: { name: 'Mi Tablero v2' },
            children: [
                {
                    path: 'edit/:policy_hash',
                    name: 'Editar Emisión',
                    // v1 cerrada: sin equivalente propio en v2 → Mis Folios v2 (mapa
                    // aprobado). Nota: `useActivityLogs.ts` ya deja de navegar aquí para
                    // el caso 'emision' en subramo auto/pickup (usa el modal v2); el resto
                    // de subramos sigue cayendo en esta ruta, que ahora redirige.
                    beforeEnter: redirectToFoliosList,
                    component: () => import('@/views/private/emission/Edit.vue')
                }
            ]
        },
        {
            path: 'multiquote',
            name: 'Multicotizador',
            redirect: { name: 'Mi Tablero v2' },
            children: [
                {
                    path: 'auto',
                    name: 'Autos Online',
                    // v1 cerrada: Autos ya navega al multicotizador v2 desde los flujos
                    // nuevos; esta URL v1 cae a Mis Folios v2 (mapa aprobado).
                    beforeEnter: redirectToFoliosList,
                    component: () => import('@/views/private/multiquote/List.vue'),
                    props: (route: RouteLocationNormalizedLoaded) => ({
                        categoryId: '1',
                        subCategoryId: '4',
                        movementType: '10',
                        searchInput: route.query.searchInput || '',
                        status: route.query.status || null,
                        model: 'multi_quote'
                    })
                },
                {
                    path: 'rc_contratista',
                    name: 'RC Contratista Online',
                    beforeEnter: redirectToFoliosList,
                    component: () => import('@/views/private/multiquote/List.vue'),
                    props: (route: RouteLocationNormalizedLoaded) => ({
                        categoryId: '3',
                        subCategoryId: '49',
                        movementType: '10',
                        searchInput: route.query.searchInput || '',
                        status: route.query.status || null,
                        model: 'multi_quote'
                    })
                },
                {
                    path: 'gmm',
                    name: 'GMM Online',
                    beforeEnter: redirectToFoliosList,
                    component: () => import('@/views/private/multiquote/List.vue'),
                    props: (route: RouteLocationNormalizedLoaded) => ({
                        categoryId: '2',
                        subCategoryId: '15',
                        movementType: '10',
                        searchInput: route.query.searchInput || '',
                        status: route.query.status || null,
                        model: 'multi_quote'
                    })
                },
                {
                    path: 'camiones_individual',
                    name: 'Camiones Online',
                    beforeEnter: redirectToFoliosList,
                    component: () => import('@/views/private/multiquote/List.vue'),
                    props: (route: RouteLocationNormalizedLoaded) => ({
                        categoryId: '1',
                        subCategoryId: '6',
                        movementType: '10',
                        searchInput: route.query.searchInput || '',
                        status: route.query.status || null,
                        model: 'multi_quote'
                    })
                }
            ]
        },
        {
            path: 'receipt',
            name: 'Recibos',
            redirect: { name: 'Mis Recibos Pendientes' },
            children: [
                {
                    name: 'Mis Recibos Pendientes',
                    path: 'list',
                    // v1 cerrada: sin pantalla propia en v2 → lista de Mis Pólizas v2
                    // (mapa aprobado).
                    beforeEnter: redirectToPoliciesList,
                    component: useBrandComponent('views/private/receipt/List.vue'),
                    props: (route: RouteLocationNormalizedLoaded) => ({
                        searchInput: route.query.searchInput || '',
                        filterRageDatesReceipt: route.query.filterRageDatesReceipt || '',
                        statusReceipts: route.query.statusReceipts || '1'
                    }),
                    meta: { requiresPermission: 'brokerin', minLevel: 20, requiresSystemPermission: 'my_pending_tickets' }
                }
            ]
        },
        {
            path: 'renovation',
            name: 'Renovaciones',
            redirect: { name: 'Mis Renovaciones' },
            children: [
                {
                    name: 'Mis Renovaciones',
                    path: 'list',
                    // v1 cerrada: sin pantalla propia en v2 → lista de Mis Pólizas v2
                    // (mapa aprobado).
                    beforeEnter: redirectToPoliciesList,
                    component: () => import('@/views/private/renovation/List.vue'),
                    props: (route: RouteLocationNormalizedLoaded) => ({
                        searchInput: route.query.searchInput || '',
                        filterRageDatesRenovation: route.query.filterRageDatesRenovation || '',
                        auto_renewed: route.query.auto_renewed || '',
                        subcategory_id: route.query.subcategory_id || '',
                        category_type: route.query.category_type || '',
                        filterRageDates: route.query.filterRageDates || null,
                        date_field: route.query.date_field || ''
                    }),
                    meta: { requiresPermission: 'brokerin', minLevel: 20, requiresSystemPermission: 'my_renovations' }
                }
            ]
        },
        {
            path: 'insurance',
            name: 'Aseguradoras',
            redirect: { name: 'Mis Claves de Agente' },
            children: [
                {
                    name: 'Mis Claves de Agente',
                    path: 'list',
                    // v1 cerrada: pasa a ser la pestaña "Claves" de Mi Perfil v2
                    // (hash #claves, mismo mecanismo que usa `useProfile.ts`).
                    beforeEnter: redirectToProfileClaves,
                    component: () => import('@/views/private/insurance/List.vue'),
                    props: (route: RouteLocationNormalizedLoaded) => ({
                        searchInput: route.query.searchInput || ''
                    }),
                    meta: { requiresPermission: 'brokerin', minLevel: 20 }
                },
                {
                    name: 'Configuración Aseguradora',
                    path: 'config/:insurance_id',
                    // v1 cerrada: mismo destino (pestaña "Claves" de Mi Perfil v2).
                    beforeEnter: redirectToProfileClaves,
                    component: () => import('@/views/private/insurance/Edit.vue'),
                    meta: { requiresPermission: 'brokerin', minLevel: 20 }
                }
            ]
        },
        {
            path: 'support',
            name: 'Soporte',
            redirect: { name: 'Tickets de Soporte' },
            children: [
                {
                    name: 'Tickets de Soporte',
                    path: 'list',
                    // v1 cerrada: Soporte v2 es el destino único.
                    beforeEnter: redirectToSupportList,
                    component: () => import('@/views/private/support/List.vue'),
                    props: (route: RouteLocationNormalizedLoaded) => ({
                        searchInput: route.query.searchInput || ''
                    }),
                    meta: { requiresPermission: 'brokerin', minLevel: 20, requiresSystemPermission: 'support' }
                },
                {
                    name: 'Editar Ticket',
                    path: 'config/:hash',
                    // v1 cerrada: sin detalle propio en v2 hoy → lista de Soporte v2.
                    beforeEnter: redirectToSupportList,
                    component: () => import('@/views/private/support/Edit.vue'),
                    meta: { requiresPermission: 'brokerin', minLevel: 20, requiresSystemPermission: 'support' }
                }
            ]
        },
        {
            name: 'error',
            path: 'error',
            redirect: { name: 'noExecutive' },
            children: [
                {
                    name: 'noExecutive',
                    path: 'no-executive',
                    component: () => import('@/views/private/error/NoExecutive.vue')
                }
            ]
        },
        // Rutas de tutoriales
        ...tutorialsRoutes,
        // Rediseño: convive con dashboard-main-2 hasta el switch (fase 4)
        ...miTableroRoutes,
        // Rediseño backlog 3: previsualización de Mis Pólizas v2 en /private/policies.
        // Append-only; NO cambia el menú ni la ruta v1 /private/policy. El switch va al cierre.
        ...policiesRoutes,
        // Mis Clientes v2 convive con `/private/client` hasta el switch posterior a QA.
        ...clientsRoutes,
        // Mis Folios v2 convive con `/private/activity` hasta el switch posterior a QA.
        ...activityLogsRoutes,
        // Rediseño backlog 5: previsualización de Mi Perfil v2 en /private/profile-v2.
        // Append-only; NO cambia el menú ni la ruta v1 /private/profile. El switch va al cierre.
        ...profileRoutes,
        // Rediseño backlog 6: previsualización de Centro Administrativo (Soporte) v2 en
        // /private/support-v2. Append-only; NO cambia el menú ni la ruta v1 /private/support.
        // El switch va al cierre. Permisos idénticos a v1 (brokerin · 20 · support).
        ...supportV2Routes,
        {
            path: 'without_permissions',
            name: 'NoPermissions',
            component: () => import('@/views/private/error/NoPermissions.vue')
        },
        {
            // Catch-all: cualquier URL privada no reconocida cae en Mi Tablero v2,
            // no en NoPermissions (mapa aprobado por el usuario). Sin `component`:
            // el `redirect` resuelve siempre antes de intentar montar nada.
            path: '/:pathMatch(.*)*',
            name: 'ErrorPage',
            redirect: { name: 'Mi Tablero v2' }
        }
    ]
};

export default MainRoutes;
